"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Shield, Lock, FileCheck, Database, CheckCircle2, Key, Eye, Server, Network, Activity } from "lucide-react";
import { landingCopy } from "./copy";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { LandingSectionBadge } from "./LandingSectionBadge";

const securityIcons = [Shield, Lock, FileCheck, Database, CheckCircle2, Key, Eye, Server, Network, Activity];

export default function Security() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.42, 0, 0.58, 1] as const, // easeOut cubic bezier
      },
    },
  };

  const securityColors = [
    "from-blue-500 to-cyan-500",
    "from-cyan-500 to-blue-500",
    "from-blue-600 to-blue-400",
    "from-blue-700 to-cyan-400",
    "from-cyan-600 to-blue-500",
    "from-blue-500 to-blue-700",
    "from-cyan-600 to-blue-600",
    "from-blue-400 to-cyan-400",
    "from-blue-600 to-cyan-500",
    "from-cyan-500 to-blue-600",
  ];

  return (
    <section
      id="security"
      className="relative overflow-hidden py-20 lg:py-28 bg-gradient-to-b from-background via-muted/15 to-background"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-30 dark:opacity-[0.18]"
        style={{
          backgroundImage: `linear-gradient(oklch(0.55 0.18 245 / 0.04) 1px, transparent 1px), linear-gradient(90deg, oklch(0.55 0.18 245 / 0.04) 1px, transparent 1px)`,
          backgroundSize: "48px 48px",
        }}
      />
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="mb-14 text-center lg:mb-16"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.55 }}
        >
          <div className="flex justify-center">
            <LandingSectionBadge className="mb-4">Compliance & safety</LandingSectionBadge>
          </div>
          <h2 className="mb-6 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            <span className="bg-gradient-to-r from-primary via-blue-600 to-cyan-600 bg-clip-text text-transparent">
              Security you can trust
            </span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Your financial data is protected with industry-leading security standards
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {landingCopy.security.map((item, index) => {
            const Icon = securityIcons[index] || Shield;
            const gradientClass = securityColors[index % securityColors.length];
            return (
              <motion.div key={index} variants={cardVariants}>
                <Card className="group h-full rounded-xl border border-border/70 bg-card/95 shadow-sm transition-all duration-300 hover:border-primary/25 hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${gradientClass} shadow-lg transition-transform duration-300 group-hover:scale-105`}
                      >
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <p className="text-foreground leading-relaxed pt-1">
                        {item}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Trust badges */}
        <motion.div
          className="mt-12 flex flex-wrap items-center justify-center gap-8 opacity-60"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.6 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {["ISO 9001", "ISO 27001 (ISMS)", "SOC 2 Type II", "GDPR Compliant", "PCI DSS Level 1"].map(
            (badge, index) => (
              <motion.div
                key={index}
                className="text-sm text-muted-foreground font-medium"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 0.6, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                {badge}
              </motion.div>
            )
          )}
        </motion.div>
      </div>
    </section>
  );
}

